import React from "react";
import {
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
} from "reactstrap";
import ReactFormat from "react-number-format";
import useActions from "./useActions";
import ModalHeader from "@dhn/components/molecules/modal-header";
import ActionButton from "@dhn/components/molecules/action-button";

const MONEY_PLACEHOLDER = "$0.00";

function MoneyInput({
  value,
  disabled,
  className = "",
  placeholder = MONEY_PLACEHOLDER,
  invalid,
  autoFocus,
  onValueChange,
}) {
  return (
    <ReactFormat
      value={value || ""}
      customInput={Input}
      className={`text-right ${className}`.trim()}
      fixedDecimalScale={true}
      thousandSeparator={true}
      allowNegative={false}
      decimalScale={2}
      prefix="$"
      placeholder={placeholder}
      disabled={disabled}
      invalid={invalid}
      autoFocus={autoFocus}
      onValueChange={onValueChange}
    />
  );
}

const formatMoney = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function ModalIssuanceCreditNote({
  isOpen,
  toggle,
  selectedBilling,
  monthlyClosingId,
  monthlyClosing,
  onSaved,
}) {
  const [
    {
      state,
      total,
      itbisAmount,
      loadingSaveCreditNote,
      availableAmount,
      liveRemainingAmount,
      exceedsAvailableAmount,
    },
    actions,
  ] = useActions({
    isOpen,
    toggle,
    selectedBilling,
    monthlyClosingId,
    monthlyClosing,
    onSaved,
  });

  const { handleShowReport, onChange } = actions;
  const remainingClassName =
    exceedsAvailableAmount || Number(liveRemainingAmount || 0) <= 0
      ? "text-danger"
      : "text-success";

  return (
    <Modal isOpen={isOpen} centered={true} size="lg">
      <ModalHeader
        toggle={() => toggle()}
        iconClass="fa-file-text-o"
        description="Emisión de nota de crédito"
        subtitle="Complete la descripción y el valor de la nota de crédito."
      />
      <ModalBody>
        <Row form>
          <Col md={6}>
            <FormGroup>
              <Label>Aseguradora:</Label>
              <Input type="text" value={state.insurerName || ""} placeholder="Aseguradora" disabled />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label>RNC:</Label>
              <Input type="text" value={state.insurerRnc || ""} placeholder="RNC" disabled />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label>NCF a Aplicar:</Label>
              <Input type="text" value={state.applyNcf || ""} placeholder="NCF" disabled />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label>Descripción:</Label>
          <Input
            type="textarea"
            rows={3}
            value={state.concept || ""}
            placeholder="Indique el motivo de la nota de crédito (ej. glosa de la aseguradora, ajuste de tarifa)"
            onChange={(e) => onChange("concept", e.target.value)}
          />
        </FormGroup>
        <Row form>
          <Col sm={6} md={3}>
            <FormGroup>
              <Label>Disponible:</Label>
              <MoneyInput value={availableAmount} disabled />
            </FormGroup>
          </Col>
          <Col sm={6} md={3}>
            <FormGroup>
              <Label>Valor:</Label>
              <MoneyInput
                value={state.value || ""}
                invalid={exceedsAvailableAmount}
                autoFocus={true}
                onValueChange={(values) => onChange("value", values.floatValue || "")}
              />
            </FormGroup>
          </Col>
          <Col sm={6} md={3}>
            <FormGroup>
              <Label>ITBIS:</Label>
              <MoneyInput value={itbisAmount} disabled />
            </FormGroup>
          </Col>
          <Col sm={6} md={3}>
            <FormGroup>
              <Label>Total:</Label>
              <MoneyInput value={total} disabled />
            </FormGroup>
          </Col>
        </Row>
        {exceedsAvailableAmount ? (
          <p className="text-danger small mb-0">
            El valor de la Nota de Crédito no puede exceder el balance de la Factura.
          </p>
        ) : (
          <p className="text-muted small mb-0">
            Disponible después de emitir:{" "}
            <strong className={remainingClassName}>{formatMoney(liveRemainingAmount)}</strong>
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <ActionButton
          action="confirm"
          label="Emitir"
          isLoading={loadingSaveCreditNote}
          disabled={loadingSaveCreditNote || exceedsAvailableAmount || availableAmount <= 0}
          onClick={handleShowReport}
        />
        <ActionButton action="cancel" onClick={() => toggle()} />
      </ModalFooter>
    </Modal>
  );
}
